/**
 * A Generic API adapter accepting a base url and headers. It will be implemented by any other api adapter.
 */
/* globals RequestInit */

import 'isomorphic-fetch';
import { stringify, parse } from 'query-string';
import { ErrorHandlerFunction, parseException } from './ErrorHandlers';
import { Config } from 'Common/appshell_types';

export default class GenericApiAdapter {
    private readonly headers: object;

    private readonly apiBaseUrl: string;

    private readonly config: Config;

    public initialQueryParams: {};

    private readonly userAgent: string;

    // eslint-disable-next-line max-params
    public constructor(
        apiBaseUrl: string,
        config: Config,
        userAgent: string,
        initialQueryParams: object = {},
        headers: object = {}
    ) {
        this.apiBaseUrl = apiBaseUrl;
        this.headers = headers;
        this.config = config;
        this.initialQueryParams = initialQueryParams;
        this.userAgent = userAgent;
    }

    private getHeaders(headers: object = {}): Headers {
        return new Headers({
            Accept: 'application/json',
            'x-app-version': `1.0.0-${this.config.buildVersion}`,
            'x-app-type': 'bella',
            'x-device-type': 'phone',
            'x-client-user-agent': this.userAgent,
            ...this.headers,
            ...headers,
        });
    }

    private getQueryString(queryParams: object): string {
        const mergedQueryParams = { ...this.initialQueryParams, ...queryParams };
        return `?${stringify(mergedQueryParams, { arrayFormat: 'comma' })}`;
    }

    // eslint-disable-next-line max-params
    public async get<T>(
        url: string,
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction,
        fullResponse = false
    ): Promise<T> {
        const queryString = this.getQueryString(queryParams);
        try {
            const response = await fetch(this.apiBaseUrl + encodeURI(url) + queryString, {
                method: 'GET',
                headers: this.getHeaders(headers),
                credentials: 'same-origin',
            });

            if (!response.ok) {
                errorHandler(url, { query: parse(queryString) }, response);
            }
            if (fullResponse) {
                return response as any;
            }
            return response.json();
        } catch (error) {
            throw parseException(error, url, { query: parse(queryString) });
        }
    }

    // eslint-disable-next-line max-params
    public async post(
        url: string,
        body: RequestInit['body'],
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction
    ): Promise<Response> {
        try {
            // we need to keep the await here, because otherwise we cannot catch an error,
            // because it directly return the promise.
            const response = await fetch(this.apiBaseUrl + encodeURI(url) + this.getQueryString(queryParams), {
                method: 'POST',
                headers: this.getHeaders(headers),
                body,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                errorHandler(url, { body, query: queryParams }, response);
            }

            return response;
        } catch (error) {
            throw parseException(error, url, { body, query: queryParams });
        }
    }

    // eslint-disable-next-line max-params
    public async delete(
        url: string,
        body: RequestInit['body'],
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction
    ): Promise<Response> {
        try {
            const response = await fetch(this.apiBaseUrl + encodeURI(url) + this.getQueryString(queryParams), {
                method: 'DELETE',
                headers: this.getHeaders(headers),
                body,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                errorHandler(url, { body, query: queryParams }, response);
            }

            return response;
        } catch (error) {
            throw parseException(error, url, { body, query: queryParams });
        }
    }
}
